import React, { useMemo } from 'react';
import css from './StockAvailabilityCalendar.css';

/**
 * StockAvailabilityCalendar component displays a monthly calendar grid
 * with stock indicators (managed, available, booked, backup) for each day.
 * Updated for React v19 compatibility (no prop-types, defaults in destructuring).
 */
const StockAvailabilityCalendar = ({ 
  timeSlotsData = {}, 
  listingTitle = 'Produkt', 
  currentDate = new Date(), 
  onMonthChange = () => {} 
}) => {
  // Parse and map time slots data into a fast lookup dictionary by start date string (YYYY-MM-DD)
  const timeSlotsMap = useMemo(() => {
    const slotsArray = timeSlotsData?.data?.data || timeSlotsData?.data || [];
    const map = {};

    slotsArray.forEach(slot => {
      // Slot start is provided as strictly 'YYYY-MM-DD' from SDK payload
      const dateKey = slot.attributes?.start;
      if (dateKey) {
        map[dateKey] = slot.attributes;
      }
    });

    return map;
  }, [timeSlotsData]);

  // Extract year and month for calendar generation
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Navigation handlers triggering parent state change and API refetch
  const handlePrevMonth = () => {
    onMonthChange(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    onMonthChange(new Date(year, month + 1, 1));
  };

  // Format month and year for header display
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];
  const currentMonthLabel = `${monthNames[month]} ${year}`;

  // Calculate days for the grid matrix (including padding for the first week day)
  const calendarDays = useMemo(() => {
    const firstDayIndex = new Date(year, month, 1).getDay();
    // Adjust so week starts on Monday (1 = Mon, 0 = Sun -> 6 = Sun)
    const adjustedFirstDayIndex = firstDayIndex === 0 ? 6 : firstDayIndex - 1;
    const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();

    const daysList = [];

    // Padding empty slots for previous month offset
    for (let i = 0; i < adjustedFirstDayIndex; i++) {
      daysList.push({ dayNumber: null, dateKey: null });
    }

    // Actual days of the month matched with the string format 'YYYY-MM-DD'
    for (let day = 1; day <= daysInCurrentMonth; day++) {
      const formattedMonth = String(month + 1).padStart(2, '0');
      const formattedDay = String(day).padStart(2, '0');
      const dateKey = `${year}-${formattedMonth}-${formattedDay}`;

      daysList.push({
        dayNumber: day,
        dateKey,
        slotData: timeSlotsMap[dateKey] || null,
      });
    }

    return daysList;
  }, [year, month, timeSlotsMap]);

  return (
    <div className={css.calendarContainer}>
      {/* Calendar header control row */}
      <div className={css.calendarHeader}>
        <div className={css.titleWrapper}>
          <h2 className={css.calendarTitle}>Kalendarz dostępności</h2>
          <span className={css.productSubtitle}>{listingTitle}</span>
        </div>
        <div className={css.navigationControls}>
          <button
            type="button"
            className={css.navBtn}
            onClick={handlePrevMonth}
            aria-label="Poprzedni miesiąc"
          >
            ← Poprzedni
          </button>
          <span className={css.currentMonthText}>{currentMonthLabel}</span>
          <button
            type="button"
            className={css.navBtn}
            onClick={handleNextMonth}
            aria-label="Następny miesiąc"
          >
            Następny →
          </button>
        </div>
      </div>

      {/* Weekday labels row */}
      <div className={css.weekdaysRow}>
        <span>Pon</span>
        <span>Wt</span>
        <span>Śr</span>
        <span>Czw</span>
        <span>Pt</span>
        <span>Sob</span>
        <span>Ndz</span>
      </div>

      {/* Days grid layout */}
      <div className={css.daysGrid}>
        {calendarDays.map((item, index) => {
          if (!item.dayNumber) {
            return <div key={`empty-${index}`} className={css.emptyDayCell} />;
          }

          const { slotData, dateKey } = item;
          const isOpened = slotData ? slotData.opened : false;
          const managed = slotData ? slotData.managed : 0;
          const available = slotData ? slotData.available : 0;
          const booked = slotData ? slotData.booked : 0;
          const backup = slotData ? slotData.backup : 0;

          return (
            <div
              key={dateKey}
              className={`${css.dayCell} ${!isOpened ? css.closedDayCell : ''}`}
            >
              <div className={css.dayHeader}>
                <span className={css.dayNumber}>{item.dayNumber}</span>
                <span className={`${css.statusDot} ${isOpened ? css.dotOpen : css.dotClosed}`} title={isOpened ? 'Otwarte' : 'Zamknięte'} />
              </div>

              {slotData ? (
                <div className={css.metricsList}>
                  <div className={css.metricRow} title="Zarządzany ekwipunek">
                    <span className={css.metricLabel}>Zarządzane:</span>
                    <strong className={css.metricValue}>{managed}</strong>
                  </div>
                  <div className={css.metricRow} title="Liczba dostępnych">
                    <span className={css.metricLabel}>Dostępne:</span>
                    <strong className={`${css.metricValue} ${css.valAvailable}`}>{available}</strong>
                  </div>
                  <div className={css.metricRow} title="Liczba zarezerwowanych">
                    <span className={css.metricLabel}>Zarezerwowane:</span>
                    <strong className={`${css.metricValue} ${css.valBooked}`}>{booked}</strong>
                  </div>
                  <div className={css.metricRow} title="Bufor bezpieczeństwa">
                    <span className={css.metricLabel}>Bufor (Backup):</span>
                    <strong className={`${css.metricValue} ${css.valBackup}`}>{backup}</strong>
                  </div>
                </div>
              ) : (
                <div className={css.noDataSlot}>Brak danych</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StockAvailabilityCalendar;